import { useI18n, useT } from '@kovix/shell-kit/i18n';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NAV_ICON, type NavGroup } from '@/shared/config/navigation';
import { filterCommands, filterTaskCommands } from './commands';

/**
 * Command palette "Tìm nhanh chức năng" (mockup v150 `openCommandPalette`, Ctrl K / nút ⌘ trong ô tìm kiếm — X0‑e 18/09/2026):
 * liệt kê các mục menu ĐÃ LỌC theo entitlement + capability (cùng `nav` của Shell — không lộ màn ngoài quyền), lọc theo nhãn/tag/nhóm,
 * ↑/↓/Enter điều hướng, Esc đóng. Khác ô tìm dữ liệu (GlobalSearch → search-svc): palette chỉ điều hướng màn hình, không gọi API.
 */
export function CommandPalette({ nav, open, onClose }: { nav: NavGroup[]; open: boolean; onClose: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  // Usability test 01/10/2026: thêm tác vụ thường dùng ("Lập phiếu thu", "Nhập kho"…) sau các mục menu khớp — tổng tối đa 12
  const items = useMemo(() => {
    const menu = filterCommands(nav, q, locale);
    const tasks = filterTaskCommands(nav, q, locale).filter(
      (t) => !menu.some((m) => m.path === t.path && m.text === t.text),
    );
    return [...menu, ...tasks].slice(0, 12);
  }, [nav, q, locale]);

  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => input.current?.focus(), 20);
    }
  }, [open]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: q là tín hiệu kích hoạt — đổi chuỗi tìm thì đưa lựa chọn về dòng đầu
  useEffect(() => {
    setActive(0);
  }, [q]);

  if (!open) return null;
  const go = (path: string) => {
    onClose();
    navigate({ to: path as '/' });
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && items[active]) {
      e.preventDefault();
      go(items[active].path);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: bấm nền ngoài hộp để đóng — bàn phím đóng bằng Esc
    <div
      className="kx-cmd-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="kx-cmd-box" role="dialog" aria-modal="true" aria-label={t('shell.commandPalette')}>
        <div className="kx-cmd-search">
          <span aria-hidden>🔎</span>
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('shell.commandPlaceholder')}
            aria-label={t('shell.commandPlaceholder')}
            role="combobox"
            aria-expanded
            aria-controls="kx-cmd-list"
            aria-activedescendant={items[active] ? `kx-cmd-${items[active].key}` : undefined}
          />
          <kbd>Esc</kbd>
        </div>
        <div id="kx-cmd-list" className="kx-cmd-results" role="listbox">
          {items.length === 0 ? <div className="kx-cmd-empty">{t('shell.commandEmpty')}</div> : null}
          {items.map((it, i) => (
            <button
              key={it.key}
              id={`kx-cmd-${it.key}`}
              type="button"
              role="option"
              aria-selected={i === active}
              className={`kx-cmd-item ${i === active ? 'active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(it.path)}
            >
              <span className="kx-cmd-ico" aria-hidden>
                {NAV_ICON[it.icon] ?? '•'}
              </span>
              <span className="kx-cmd-text">
                <b>{it.text}</b>
                <small>
                  {it.group} · {it.tag}
                </small>
              </span>
              <span className="kx-cmd-go" aria-hidden>
                ↵
              </span>
            </button>
          ))}
        </div>
        <div className="kx-cmd-foot">{t('shell.commandHint')}</div>
      </div>
    </div>
  );
}
